"use client";

import { useState } from "react";
import Link from "next/link";

interface BlogPost {
  id: number;
  title: string;
  date: string;
  category: string;
  excerpt: string;
  img: string;
  slug: string;
}

interface BlogListProps {
  posts: BlogPost[];
}

export default function BlogList({ posts }: BlogListProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState<string[]>([]);

  const toggleFilter = (category: string) => {
    if (activeFilters.includes(category)) {
      setActiveFilters(activeFilters.filter((c) => c !== category));
    } else {
      setActiveFilters([...activeFilters, category]);
    }
  };

  const filteredPosts = posts.filter((post) => {
    const matchesSearch = post.title
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesFilter =
      activeFilters.length === 0 || activeFilters.includes(post.category);
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="grid grid-cols-1 md:grid-cols-[250px_1fr] gap-8 max-w-[1200px] mx-auto my-16 px-8 min-h-[60vh]">
      <aside className="border-b-[3px] md:border-b-0 md:border-r-[3px] border-[#1a1a1a] pb-8 md:pb-0 md:pr-8">
        <div className="font-bold mb-4 uppercase">Categories</div>
        <ul className="list-none">
          {["Development", "Design", "Case Studies", "Updates"].map(
            (category) => (
              <li
                key={category}
                className="py-2 cursor-pointer border-b-[2px] border-[#1a1a1a] opacity-70 hover:opacity-100 hover:underline transition-opacity"
              >
                <label className="flex items-center cursor-pointer select-none w-full">
                  <div className="relative mr-2">
                    <input
                      type="checkbox"
                      className="peer absolute opacity-0 w-0 h-0"
                      checked={activeFilters.includes(category)}
                      onChange={() => toggleFilter(category)}
                    />
                    <div className="w-5 h-5 bg-[#e4e2dd] border-[3px] border-[#1a1a1a] peer-checked:bg-[#1a1a1a] transition-colors"></div>
                  </div>
                  {category}
                </label>
              </li>
            )
          )}
        </ul>
      </aside>

      <main className="flex flex-col gap-8">
        <input
          type="text"
          className="w-full p-3 border-[3px] border-[#1a1a1a] font-mono bg-[#e4e2dd] text-[1rem] outline-none focus:bg-white"
          placeholder="Search logs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <div className="flex flex-wrap gap-2">
          {activeFilters.map((filter) => (
            <div
              key={filter}
              className="bg-[#1a1a1a] text-[#e4e2dd] px-2 py-1 text-[0.8rem] flex items-center gap-2 cursor-pointer border-[2px] border-[#1a1a1a] hover:opacity-80"
              onClick={() => toggleFilter(filter)}
            >
              <span className="font-bold">{filter}</span>
              <span>x</span>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-8">
          {filteredPosts.map((post) => (
            <Link
              href={`/blog/${post.slug}`}
              key={post.id}
              className="flex flex-col md:flex-row border-[3px] border-[#1a1a1a] bg-[#e4e2dd] transition-all duration-200 cursor-pointer relative text-inherit no-underline h-auto md:h-[200px] hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[4px_4px_0px_#1a1a1a] group"
            >
              <div
                className="w-full md:w-[250px] h-[200px] md:h-full border-b-[3px] md:border-b-0 md:border-r-[3px] border-[#1a1a1a] bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: post.img }}
              ></div>
              <div className="p-6 flex-1 flex flex-col justify-center">
                <div className="text-[0.8rem] opacity-60 mb-2 uppercase">
                  {post.date} • {post.category}
                </div>
                <h2 className="pixel-font text-[1.8rem] mb-2 leading-[1.1] group-hover:underline lowercase">
                  {post.title}
                </h2>
                <p className="text-[0.9rem] opacity-80 line-clamp-3">
                  {post.excerpt}
                </p>
              </div>
              <div className="absolute top-4 right-4 text-[1.5rem] cursor-pointer z-[2] transition-transform duration-200 hover:scale-125">
                &#9734;
              </div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
